import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProgressChart = ({ data, type = 'line', title, color = '#2D5A4F' }) => {
  const [timeframe, setTimeframe] = useState('week');

  const timeframeOptions = [
    { value: 'week', label: '7 Days' },
    { value: 'month', label: '30 Days' },
    { value: 'quarter', label: '3 Months' },
    { value: 'year', label: '1 Year' }
  ];

  const getFilteredData = () => {
    const now = new Date();
    const days = timeframe === 'week' ? 7 : timeframe === 'month' ? 30 : timeframe === 'quarter' ? 90 : 365;
    return data?.filter(item => {
      const itemDate = new Date(item.date);
      const diffTime = Math.abs(now - itemDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= days;
    });
  };

  const filteredData = getFilteredData();

  const renderChart = () => {
    switch (type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={filteredData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F5F7F5" />
              <XAxis dataKey="name" stroke="#5A6B5A" fontSize={12} />
              <YAxis stroke="#5A6B5A" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #F5F7F5',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(90, 107, 90, 0.12)'
                }}
              />
              <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
        const COLORS = ['#2D5A4F', '#7B9E87', '#E8A547', '#4A7C59', '#D4A574'];
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={filteredData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#2D5A4F"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
              >
                {filteredData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );
      
      default:
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={filteredData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F5F7F5" />
              <XAxis dataKey="name" stroke="#5A6B5A" fontSize={12} />
              <YAxis stroke="#5A6B5A" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #F5F7F5',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(90, 107, 90, 0.12)'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke={color} 
                strokeWidth={3}
                dot={{ fill: color, strokeWidth: 2, r: 6 }}
                activeDot={{ r: 8, fill: color }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-wellness">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-heading font-semibold text-foreground">{title}</h3>
        <div className="flex items-center space-x-2">
          {timeframeOptions?.map((option) => (
            <Button
              key={option?.value}
              variant={timeframe === option?.value ? "default" : "ghost"}
              size="sm"
              onClick={() => setTimeframe(option?.value)}
            >
              {option?.label}
            </Button>
          ))}
        </div>
      </div>
      <div className="w-full">
        {renderChart()}
      </div>
      <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
        <span>Showing data for last {timeframeOptions?.find(opt => opt?.value === timeframe)?.label?.toLowerCase()}</span>
        <div className="flex items-center space-x-2">
          <Icon name="TrendingUp" size={16} />
          <span>Updated 2 hours ago</span>
        </div>
      </div>
    </div>
  );
};

export default ProgressChart;